import { MonoTypeOperatorFunction, Observable, Subscription } from 'rxjs';

/** Default upper bound for a save initiated from an aid-form-actions control. */
export const FORM_ACTION_SAVE_TIMEOUT_MS = 30_000;

export class FormActionSaveTimeoutError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`The save did not complete within ${timeoutMs} milliseconds.`);
    this.name = 'FormActionSaveTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export class FormActionSaveEmptyError extends Error {
  constructor() {
    super('The save completed without returning a successful result.');
    this.name = 'FormActionSaveEmptyError';
  }
}

export interface FormActionSaveTimeoutOptions<T> {
  timeoutMs?: number;
  isSuccessfulResult?: (value: T) => boolean;
}

/**
 * Bounds one complete save attempt without changing the global HTTP timeout
 * policy. A source emission does not reset the deadline; this matters for
 * upload streams that report progress while never producing a final result.
 */
export function withFormActionSaveTimeout<T>(
  timeoutOrOptions:
    | number
    | FormActionSaveTimeoutOptions<T> = FORM_ACTION_SAVE_TIMEOUT_MS
): MonoTypeOperatorFunction<T> {
  const options =
    typeof timeoutOrOptions === 'number'
      ? { timeoutMs: timeoutOrOptions }
      : timeoutOrOptions;
  const timeoutMs = options.timeoutMs ?? FORM_ACTION_SAVE_TIMEOUT_MS;
  const duration =
    Number.isFinite(timeoutMs) && timeoutMs > 0
      ? timeoutMs
      : FORM_ACTION_SAVE_TIMEOUT_MS;
  const isSuccessfulResult = options.isSuccessfulResult ?? (() => true);

  return source =>
    new Observable<T>(subscriber => {
      let sourceSubscription: Subscription | undefined;
      let settled = false;
      let successfulResult = false;
      const timeoutHandle = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        sourceSubscription?.unsubscribe();
        subscriber.error(new FormActionSaveTimeoutError(duration));
      }, duration);

      sourceSubscription = source.subscribe({
        next: value => {
          if (settled) {
            return;
          }
          try {
            if (isSuccessfulResult(value)) {
              successfulResult = true;
            }
          } catch (error) {
            settled = true;
            clearTimeout(timeoutHandle);
            sourceSubscription?.unsubscribe();
            subscriber.error(error);
            return;
          }
          subscriber.next(value);
        },
        error: error => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timeoutHandle);
          subscriber.error(error);
        },
        complete: () => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timeoutHandle);
          if (!successfulResult) {
            subscriber.error(new FormActionSaveEmptyError());
            return;
          }
          subscriber.complete();
        }
      });

      return () => {
        settled = true;
        clearTimeout(timeoutHandle);
        sourceSubscription?.unsubscribe();
      };
    });
}
